import { InputFlag } from "odyssey-shared";

/**
 * What the page reports for idle and AFK (ADR-090 decision 3): whether the player pressed a key or a pointer button on
 * the page since the last input, read by one capture listener for each on the window, and whether the page is hidden
 * or without focus now. It reports facts, never a state; the map server times every threshold. Wheel and pointer
 * moves are not use, so the camera never counts.
 */

/**
 * Where the listeners go: the window.
 */
export interface ActivityTarget {
  addEventListener(type: "keydown" | "pointerdown", listener: () => void, options: { readonly capture: boolean }): void;
  removeEventListener(type: "keydown" | "pointerdown", listener: () => void, options: { readonly capture: boolean }): void;
}

/**
 * Where the page's visibility and focus are read: the document.
 */
export interface ActivityPage {
  readonly hidden: boolean;
  hasFocus(): boolean;
}

const CAPTURE = { capture: true } as const;

/**
 * The page's activity, for the input's Used and Away bits.
 *
 * @example
 * ```ts
 * const activity = new PageActivity(window, document);
 * encodeInput(buffer, { seq, ackTick, dir, flags: activity.take() });
 * activity.detach();
 * ```
 */
export class PageActivity {
  private used = false;

  /**
   * Marks the page used, bound once.
   */
  private readonly mark = (): void => {
    this.used = true;
  };

  /**
   * Listens on the target from now on.
   *
   * @param target The window.
   * @param page The document.
   */
  public constructor(
    private readonly target: ActivityTarget,
    private readonly page: ActivityPage,
  ) {
    target.addEventListener("keydown", this.mark, CAPTURE);
    target.addEventListener("pointerdown", this.mark, CAPTURE);
  }

  /**
   * The input's bits now: Used when a key or a pointer button was pressed since the last call, which it forgets, and
   * Away when the page is hidden or has no focus.
   *
   * @returns The flags.
   */
  public take(): number {
    let flags = this.used ? InputFlag.Used : 0;
    if (this.page.hidden || !this.page.hasFocus()) {
      flags |= InputFlag.Away;
    }
    this.used = false;
    return flags;
  }

  /**
   * Forgets the presses so far, as sending `/afk` does: the keys that typed it are not use (ADR-090 decision 5).
   */
  public forget(): void {
    this.used = false;
  }

  /**
   * Stops listening.
   */
  public detach(): void {
    this.target.removeEventListener("keydown", this.mark, CAPTURE);
    this.target.removeEventListener("pointerdown", this.mark, CAPTURE);
  }
}
